import React, { useCallback, useEffect } from 'react'
import { makeStyles } from '@mui/styles'
import { Formik, Form, FormikHelpers } from 'formik'
import * as Yup from 'yup'
import PerfectScrollbar from 'react-perfect-scrollbar'

import ButtonIconSquare from '@/modules/base/components/buttons/icon-square/button-icon-square'
import { submitCompanyData, fetchCompanyData } from '@/redux/ducks/settings/company'
import lang from '@/lang'
import { Title, SubTitle } from '@/modules/base/components/texts'
import { TextInput, ZipCodeInput } from '@/modules/base/components/inputs'
import useClientSetting from '@/modules/client/_helpers/useClientSetting'
import useCheckAccessRights from '@/modules/_helpers/accessCheck/useCheckAccessRights'
import { USER_RIGHTS, customerTypes } from '@/modules/base/util/constants'
import { isMeinBuero } from '@/utils/build-env'
import OMOLogo from '@assets/images/logo/svg/logo.svg'
import MBOLogo from '@assets/images/logo/svg/logoV2.svg'
import RichTooltip from '@/modules/base/components/rich-tooltip/rich-tooltip'
import staffService from '@/modules/_services/staffService'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import { useAppDispatch, useAppSelector } from '@/redux/store'

const isMeinbuero = isMeinBuero()
const logo = isMeinbuero ? MBOLogo : OMOLogo

const validationSchema = Yup.object({
  companyName: Yup.string().required(lang.staff.missingDataModal.required),
  street: Yup.string().required(lang.staff.missingDataModal.required),
  zipCode: Yup.string().required(lang.staff.missingDataModal.required),
  city: Yup.string().required(lang.staff.missingDataModal.required),
  phone1: Yup.string().required(lang.staff.missingDataModal.required),
  email: Yup.string().required(lang.staff.missingDataModal.required),
})

const useStyles = makeStyles(() => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    padding: '0px 50px 0px 50px',
    maxHeight: '55vh',
  },
  row: {
    display: 'flex',
    padding: '10px 0px',

    '& > *:first-child': {
      paddingRight: 25,
    },
    '& > *:last-child': {
      paddingLeft: 25,
    },
    '& > *:first-child:last-child': {
      padding: 0,
    },
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: 25,
    gap: 10,
    width: '100%',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingTop: 30,
  },
  buttons: {
    width: 150,
  },
  cancelButton: {
    width: 150,
    textAlign: 'center',
    cursor: 'pointer',
  },
}))

interface State {
  settings: { company: any };
  tenant: { info: any };
}

interface CompanyDataProps {
  onSave?: () => void;
  onClose?: () => void;
}

const MissingStaffDataLeft: React.FC<CompanyDataProps> = (props) => {
  const classes = useStyles()
  const dispatch = useAppDispatch()
  const tenantInfo = useAppSelector((state: State) => state.tenant.info)
  const companyData = useAppSelector((state: State) => state.settings.company)
  const { isLoading, zipCode, city } = companyData
  const { onSave, onClose } = props

  const identifier = tenantInfo?.subscriptions?.some((s: any) => s.isTaxConsultant) ? customerTypes.PERSON : customerTypes.COMPANY

  const { client, isClientSetting } = useClientSetting()
  const readOnly = isClientSetting ? false : !useCheckAccessRights(USER_RIGHTS.SETTINGS.WRITE)

  const handleOpenStaffPage = async(): Promise<void> => {
    await staffService.openStaffPage()
  }

  const handleSubmit = useCallback((values: any, { setSubmitting }: FormikHelpers<any>) => {
    dispatch(submitCompanyData({ ...values }, true, client?.id))
    setSubmitting(false)
    onSave?.()
    handleOpenStaffPage()
  }, [dispatch, client?.id, props])

  useEffect(() => {
    dispatch(fetchCompanyData(client?.id))
  }, [dispatch, client?.id])

  return (
    <>
      <div className={classes.header}>
        <SVGIcon height="55px" width="200px" svg={logo} />
        <Title>{lang.staff.missingDataModal.title}</Title>
        <SubTitle>{lang.staff.missingDataModal.subTitle}</SubTitle>
      </div>
      <Formik
        initialValues={companyData}
        onSubmit={handleSubmit}
        enableReinitialize={true}
        validationSchema={validationSchema}
        validateOnMount={true}
      >
        {({ isSubmitting, isValid }) => {

          return (
            <Form>
              <PerfectScrollbar>
                <div className={classes.wrapper}>
                  <div className={classes.row}>
                    <TextInput label={lang[`${identifier}NameLabel`]} name='companyName' useFormik readOnly={readOnly} />
                  </div>
                  <div className={classes.row}>
                    <TextInput
                      name='street'
                      label={lang.streetFieldLabel}
                      readOnly={readOnly}
                      useFormik />
                  </div>
                  <div className={classes.row}>
                    <ZipCodeInput readOnly={readOnly} zipCode={zipCode} city={city} useFormik />
                  </div>
                  <div className={classes.row}>
                    <TextInput
                      name='phone1'
                      label={lang.customerContactForm.phone1}
                      useFormik
                    />
                  </div>
                  <div className={classes.row}>
                    <TextInput
                      name='email'
                      label={lang.customerContactForm.email}
                      useFormik
                    />
                  </div>
                </div>
              </PerfectScrollbar>
              <RichTooltip disabled={isValid} content={<span dangerouslySetInnerHTML={{ __html: lang.staff.missingDataModal.submitButtonTooltip }} />} width={260} placement={'top'}>
                <div className={classes.buttonRow}>
                  <ButtonIconSquare
                    className={classes.buttons}
                    label={lang.staff.missingDataModal.submitButton}
                    type='submit'
                    disabled={readOnly || isLoading || isSubmitting || !isValid}
                  />
                  <div
                    className={classes.cancelButton}
                    onClick={() => onClose?.()}
                  >
                    {lang.staff.missingDataModal.cancelButton}
                  </div>
                </div>
              </RichTooltip>
            </Form>
          )
        }}
      </Formik>
    </>
  )

}

export default MissingStaffDataLeft
